/**
 * Lightweight typing signal for deferring non-critical work during keyboard input.
 *
 * Uses a globalThis flag (not Vue reactivity) to avoid adding reactive overhead.
 * Consumers call signalTyping() on input events; background tasks check isTyping()
 * and defer work until typing stops.
 */

let typingTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Signal that the user is actively typing.
 * Sets a flag that auto-clears after `durationMs` of inactivity.
 */
export function signalTyping(durationMs = 500): void {
  globalThis.__isTyping = true
  if (typingTimer !== null) {
    clearTimeout(typingTimer)
  }
  typingTimer = setTimeout(() => {
    globalThis.__isTyping = false
    typingTimer = null
  }, durationMs)
}

/**
 * Check if the user is currently typing.
 */
export function isTyping(): boolean {
  return globalThis.__isTyping === true
}

/**
 * Returns a promise that resolves when typing stops (polls every 100ms).
 * Resolves immediately if not currently typing.
 * Has a safety timeout to prevent indefinite deferral (e.g., continuous paste/autofill).
 */
export function waitForTypingEnd(maxWaitMs = 5000): Promise<void> {
  if (!isTyping()) {
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    const start = Date.now()
    const check = () => {
      if (!isTyping() || Date.now() - start > maxWaitMs) {
        resolve()
      } else {
        setTimeout(check, 100)
      }
    }
    setTimeout(check, 100)
  })
}
